import {
  LockOutlined as LockOutlinedIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Chip, Grid } from '@mui/material';
import { useContext, useRef, useState } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { AssemblyLayout } from 'src/components/assemblyLayout';
import { AssemblyScreen } from 'src/components/assemblyScreen';
import { AssemblyScreenSection } from 'src/components/assemblyScreenSection';
import { AuthDataProvider } from 'src/components/authProvider';
import { Breadcrumbs } from 'src/components/breadcrumbs';
import { generateGridLayout } from 'src/components/generateGridLayout/GenerateGridLayout';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import {
  AssemblyJourneys,
  CodJourney,
  SectionJourney,
} from 'src/constants/assemblyJourneys';
import { TAKEOVER_CONFIG } from 'src/constants/gcfSectionConfig';
import { PATHS } from 'src/constants/paths';
import { useWindow } from 'src/hooks';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import {
  GlobalComponentsScreenInterface,
  ILayout,
  IResponseType,
  IStatus,
  SectionTypes,
} from 'src/interfaces';
import {
  getResponsiveScale,
  isCurrentScreenWithDYR,
  isDisabledScreen,
  lang,
  useUserRole,
} from 'src/utils';
import { getSectionName } from 'src/utils/section-name-helper';

import {
  ActionButtonsContainer,
  AssemblyScreenWrapper,
  ChipSettingsContainer,
  GridContainer,
  HeaderContainer,
  LayoutNameText,
  StyledLayoutEditContentContainer,
} from '../../EditLayoutsPage.styled';

export interface ILayoutEditContentContainer {
  layouts: ILayout[];
  formMethods: UseFormReturn<FieldValues, any, undefined>;
  canSaveAsDraft: boolean;
  isSaveAsReadyLoading: boolean;
  hasDYR: boolean;
  screenNames: string[];
  isLayoutReadyToSave: () => boolean;
  handleOpenConfig: (index: number) => void;
  handleSave: (status: IStatus) => void;
  updateLayout: { isLoading: boolean };
  discardModal: { onOpenModal: () => void };
  activeAssemblyJourney?: AssemblyJourneys;
  sectionTypes?: SectionTypes[];
  globalComponentsScreen?: GlobalComponentsScreenInterface;
}

export const LayoutEditContentContainer = ({
  layouts,
  canSaveAsDraft,
  formMethods,
  isSaveAsReadyLoading,
  hasDYR,
  screenNames,
  isLayoutReadyToSave,
  activeAssemblyJourney,
  handleOpenConfig,
  handleSave,
  updateLayout,
  sectionTypes,
  discardModal,
  globalComponentsScreen,
}: ILayoutEditContentContainer) => {
  const { isViewOnly } = useUserRole();
  const { userRole } = useContext(AuthDataProvider);
  const layoutName = formMethods.watch('name');
  const containerRef = useRef<HTMLDivElement>(null);
  const { containerWidth } = useWindow(containerRef);
  const [hasModified, setHasModified] = useState(false);

  const [defaultLayout] = layouts;
  const isCodConfirm =
    defaultLayout?.config?.sectionTypes?.includes(SectionTypes.CONFIRMATION) &&
    activeAssemblyJourney === CodJourney.PURCHASE_IS_CONFIRMED;

  return (
    <StyledLayoutEditContentContainer ref={containerRef}>
      <HeaderContainer>
        <Box flex={1} overflow={'hidden'}>
          {userRole !== AuthClassification.OWNER_OPERATOR && (
            <Breadcrumbs
              items={[
                { label: lang.layouts, link: PATHS.ASSEMBLY_LAYOUTS },
                { label: lang.layoutBuilder },
              ]}
            />
          )}
          <CustomTooltip
            placement="bottom"
            title={layoutName}
            width={500}
            overflowLineNumber={1}>
            <LayoutNameText variant="h3">{layoutName}</LayoutNameText>
          </CustomTooltip>
        </Box>
        {isViewOnly ? (
          <ViewOnlyAlert />
        ) : (
          <ActionButtonsContainer spacing={2} direction="row">
            <LoadingButton
              variant="text"
              loading={!isSaveAsReadyLoading && updateLayout.isLoading}
              onClick={() => {
                handleSave(IStatus.DRAFT);
                setHasModified(true);
              }}
              disabled={
                !canSaveAsDraft ||
                (!formMethods.formState.isDirty && hasModified)
              }>
              {lang.saveAsDraft}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={isSaveAsReadyLoading && updateLayout.isLoading}
              onClick={discardModal.onOpenModal}
              disabled={!isLayoutReadyToSave() || !layoutName}>
              {lang.save}
            </LoadingButton>
          </ActionButtonsContainer>
        )}
      </HeaderContainer>

      <GridContainer container spacing={2}>
        {layouts.map((layout) => {
          const scale = getResponsiveScale(
            containerWidth,
            layout.screens,
            IResponseType.PAGE,
          );

          return (
            <Grid item xs key={layout.id}>
              <AssemblyLayout>
                {layout.screens.map((screen, index) => {
                  let screenSections = screen?.sections ?? [];

                  const isScreenDisabled = isDisabledScreen(
                    index,
                    sectionTypes,
                    activeAssemblyJourney,
                    globalComponentsScreen,
                  );
                  if (formMethods.watch(`screen_${index}`)) {
                    const makeTakeOver = {
                      height: screen.height,
                      width: screen.width,
                      left_offset: 0,
                      top_offset: 0,
                      id: 1,
                      name: `local takeover ${index}`,
                      config: TAKEOVER_CONFIG,
                      created_at: 'time',
                      modified_at: 'time',
                    };

                    screenSections = [...screenSections, makeTakeOver];
                  } else {
                    screenSections = screen.sections.filter(
                      (section) =>
                        !(
                          section.height === screen.height &&
                          section.width === screen.width &&
                          section.config?.events?.LAYOUT_TIMER !== undefined
                        ),
                    );
                  }

                  return (
                    <AssemblyScreenWrapper key={screen.layoutScreenId}>
                      <ChipSettingsContainer>
                        <Chip
                          label={screenNames[index]}
                          icon={
                            isScreenDisabled ? (
                              <LockOutlinedIcon color="secondary" />
                            ) : (
                              <></>
                            )
                          }
                        />
                        {activeAssemblyJourney !== SectionJourney.TAKE_OVER &&
                          activeAssemblyJourney !==
                            SectionJourney.SECTION_ROTATION && (
                            <SettingsIcon
                              color="secondary"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleOpenConfig(index)}
                            />
                          )}
                      </ChipSettingsContainer>
                      <AssemblyScreen
                        key={screen.id}
                        height={screen.height}
                        width={screen.width}
                        scale={scale}
                        selectable={false}
                        type="editor">
                        {screen.name.includes('placeholder')
                          ? generateGridLayout(
                              layout?.config?.gridLines?.[index] ?? '',
                              scale,
                              hasDYR &&
                                isCurrentScreenWithDYR(
                                  screen.channel_screen_sequence,
                                  globalComponentsScreen,
                                ),
                            )
                          : screenSections?.map((section) => (
                              <CustomTooltip
                                key={section.screenSectionId}
                                placement="right"
                                title={lang.disableScreenTitle}
                                cursor={
                                  isScreenDisabled ? 'not-allowed' : 'default'
                                }
                                width={526}
                                isHidden={!isScreenDisabled ? true : undefined}
                                skipValidateOverflow>
                                <AssemblyScreenSection
                                  section={section}
                                  sectionTypes={layout.config?.sectionTypes}
                                  activeAssemblyJourney={activeAssemblyJourney}
                                  isCodConfirmationOpen={isCodConfirm}
                                  scale={scale}
                                  sectionName={getSectionName(
                                    section,
                                    screen,
                                    sectionTypes,
                                    activeAssemblyJourney,
                                    globalComponentsScreen,
                                  )}
                                  selectable={false}
                                  showEditIcon={false}
                                  isDisabled={isScreenDisabled}
                                  isLayoutList
                                  type="grid"
                                />
                              </CustomTooltip>
                            ))}
                      </AssemblyScreen>
                    </AssemblyScreenWrapper>
                  );
                })}
              </AssemblyLayout>
            </Grid>
          );
        })}
      </GridContainer>
    </StyledLayoutEditContentContainer>
  );
};
